'use client';

import * as React from 'react';

function cn(...cls: (string | false | null | undefined)[]) {
  return cls.filter(Boolean).join(' ');
}

type CalcState = {
  emp: number;
  hrCost: number;
  ppm: number;
  events: number;
  eventFee: number;
  edoCost: number;
};

type FormData = {
  name: string;
  phone: string;
  task: string;
  email: string;
  agreed: boolean;
  formName: string;
};

const nf = new Intl.NumberFormat('ru-RU');
const cf = new Intl.NumberFormat('ru-RU', { style: 'currency', currency: 'RUB', maximumFractionDigits: 0 });

function getStatusColorClass(status: string) {
  if (status.includes('Ошибка') || status.includes('сети')) return 'text-red-600 font-medium';
  if (status.includes('Пожалуйста')) return 'text-amber-600 font-medium';
  if (status.includes('успешно')) return 'text-green-600 font-medium';
  return 'text-slate-500';
}

function useCalc(defaults: CalcState) {
  const [state, setState] = React.useState<CalcState>(() => {
    if (typeof window === 'undefined') return defaults;
    try {
      const raw = localStorage.getItem('hr_load_calc');
      return raw ? { ...defaults, ...JSON.parse(raw) } : defaults;
    } catch {
      return defaults;
    }
  });

  React.useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('hr_load_calc', JSON.stringify(state));
    }
  }, [state]);

  const service = state.emp * state.ppm + state.events * state.eventFee + state.edoCost;
  const current = state.hrCost;
  const saving = current - service;
  const savingPct = current > 0 ? Math.max(0, saving) / current : 0;

  return { state, setState, service, current, saving, savingPct };
}

function Section({
  id,
  gray,
  className,
  children,
}: React.PropsWithChildren<{ id?: string; gray?: boolean; className?: string }>) {
  return (
    <section id={id} className={cn('py-20 box-border', gray && 'bg-slate-50', className)}>
      <div className="mx-auto max-w-6xl px-6">{children}</div>
    </section>
  );
}

function Card({ className, children }: React.PropsWithChildren<{ className?: string }>) {
  return (
    <div className={cn('rounded-2xl bg-white p-6 shadow-[0_6px_18px_rgba(0,0,0,0.06)]', className)}>
      {children}
    </div>
  );
}

function KPI({ icon, value, label }: { icon: React.ReactNode; value: string; label: string }) {
  return (
    <div className="flex items-center gap-3 rounded-xl border border-white/30 bg-white/10 px-4 py-2 text-white backdrop-blur h-[72px] overflow-hidden">
      <div className="flex h-9 w-9 items-center justify-center rounded-full bg-white/60 text-base">{icon}</div>
      <div>
        <div className="text-xl font-extrabold leading-none">{value}</div>
        <div className="text-sm opacity-90 leading-none">{label.replace('*', '')}</div>
      </div>
    </div>
  );
}

function LabeledInputNumber({
  label,
  value,
  onChange,
  min,
  step = 1,
}: {
  label: string;
  value: number;
  onChange: (n: number) => void;
  min?: number;
  step?: number;
}) {
  return (
    <label className="block">
      <span className="text-sm text-slate-600">{label}</span>
      <input
        type="number"
        value={Number.isFinite(value) ? value : 0}
        min={min}
        step={step}
        onChange={(e) => onChange(Number(e.target.value || 0))}
        className="mt-1 w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-blue-600 focus:outline-none"
      />
    </label>
  );
}

function StatCard({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <Card>
      <div className="text-xs text-slate-500">{label}</div>
      <div className="text-2xl font-extrabold text-[#003B9B]">{value}</div>
      {hint && <div className="text-xs text-slate-500">{hint}</div>}
    </Card>
  );
}

export default function HRLoadReductionPage() {
  const { state, setState, service, current, saving, savingPct } = useCalc({
    emp: 120,
    hrCost: 350_000,
    ppm: 600,
    events: 80,
    eventFee: 250,
    edoCost: 15_000,
  });

  const [formData, setFormData] = React.useState<FormData>({
    name: '',
    phone: '',
    task: '',
    email: '',
    agreed: false,
    formName: 'Снижение кадровой нагрузки',
  });

  const [formStatus, setFormStatus] = React.useState('');

  const handleSubmit = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    if (!formData.agreed) {
      setFormStatus('Пожалуйста, дайте согласие на обработку персональных данных.');
      return;
    }

    if (!formData.name.trim() || !formData.phone.trim() || !formData.email.trim()) {
      setFormStatus('Пожалуйста, заполните Имя, Телефон и Email для отправки заявки.');
      return;
    }

    setFormStatus('Отправка...');

    const apiUrl = `${process.env.NEXT_PUBLIC_STRAPI_API_URL}/api/hr-load-requests`;

    try {
      const payload = {
        name: formData.name,
        phone: formData.phone,
        task: formData.task,
        email: formData.email,
        agreed: formData.agreed,
        formName: formData.formName, 
      };

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ data: payload }),
      });

      if (response.ok) {
        setFormStatus('Ваша заявка успешно отправлена! Скоро мы с вами свяжемся.');
        setFormData({ ...formData, name: '', phone: '', task: '', email: '' });
      } else {
        const errorData = await response.json();
        const errorMessage = errorData.error?.message || 'Произошла неизвестная ошибка.';
        setFormStatus(`Ошибка: ${errorMessage}`);
        console.error('Ошибка Strapi:', errorData);
      }
    } catch (error) {
      setFormStatus('Ошибка сети. Пожалуйста, попробуйте снова.');
      console.error('Ошибка fetch:', error);
    }
  };

  return (
    <main className="bg-white text-slate-900">
      <section className="mt-20 bg-gradient-to-br from-[#00214F] to-[#003B9B] text-white pt-24 pb-20 box-border">
        <div className="mx-auto max-w-6xl pl-1 pr-6">
          <h1 className="text-4xl font-black md:text-5xl">Снижение кадровой нагрузки</h1>
          <p className="mt-3 max-w-3xl text-base md:text-lg opacity-95 leading-tight">
            Передадим рутину HR на внешнюю команду: кадровое делопроизводство, табели и 1С:ЗУП, ЭДО и справки,
            служба поддержки HR. Прозрачные SLA и отчётность.
          </p>
          <div className="mt-6 flex flex-col gap-3 sm:flex-row">
            <a
              href="#calc"
              className="w-full sm:w-auto inline-flex items-center justify-center bg-[#FF7C22] text-white py-3 px-5 rounded-xl font-bold transition-all duration-200 ease-in-out hover:brightness-[105%] hover:-translate-y-[1px] text-base shadow-lg"
            >
              Рассчитать эффект
            </a>
            <a
              href="#scope"
              className="w-full sm:w-auto inline-flex items-center justify-center bg-white text-[#003B9B] border-2 border-[#003B9B] py-3 px-5 rounded-xl font-bold transition-all duration-200 ease-in-out hover:brightness-[105%] hover:-translate-y-[1px] text-base shadow-lg"
            >
              Что берём на себя
            </a>
          </div>
          <div className="mt-7 rounded-2xl border border-white/30 bg-white/10 p-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <KPI icon={<span>💬</span>} value="≤ 30 мин" label="первый ответ helpdesk (9–19, пн–пт)" />
              <KPI icon={<span>📧</span>} value="≥ 95%" label="тикетов в срок SLA" />
              <KPI icon={<span>📄</span>} value="e-подписи" label="кадровые приказы и справки" />
              <KPI icon={<span>⏰</span>} value="до −40%" label="меньше рутины HR" />
            </div>
            <p className="mt-2 text-sm text-white/80">
              * Оценка зависит от объёма задач и процессов у заказчика.
            </p>
          </div>
        </div>
      </section>

      <Section id="scope" gray>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          <Card>
            <h3 className="text-xl font-extrabold">КДП и табели</h3>
            <ul className="mt-3 list-disc space-y-1 pl-5 text-base text-black">
              <li>Приёмы/переводы/увольнения, отпуска и больничные</li>
              <li>Табели и графики смен, передача в 1С:ЗУП</li>
              <li>Справки (2-НДФЛ, по месту требования), шаблоны</li>
              <li>Ведение личных дел и архивов (электронно/бумага)</li>
            </ul>
          </Card>
          <Card>
            <h3 className="text-xl font-extrabold">Helpdesk HR</h3>
            <ul className="mt-3 list-disc space-y-1 pl-5 text-base text-black">
              <li>Единый канал: портал/почта/мессенджер</li>
              <li>Категории заявок и приоритеты (P1/P2/P3)</li>
              <li>SLA: первый ответ ≤ 30 мин; резолв от 4 ч</li>
              <li>CSAT и ежемесячная аналитика по тематикам</li>
            </ul>
          </Card>
          <Card>
            <h3 className="text-xl font-extrabold">Интеграции и ЭДО</h3>
            <ul className="mt-3 list-disc space-y-1 pl-5 text-base text-black">
              <li>1С:ЗУП (обмен табелями/начислениями)</li>
              <li>ЭДО (Диадок/СБИС и др.) и КЭП</li>
              <li>СКУД/Табель/HR-портал — обмен данными</li>
              <li>Шаблоны приказов, автозаполнение реквизитов</li>
            </ul>
          </Card>
        </div>
      </Section>

      <Section id="calc">
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <Card>
            <h3 className="text-xl font-extrabold">Калькулятор эффекта</h3>
            <p className="mt-1 text-xs text-slate-500">
              Черновая оценка экономии при передаче рутины на аутсорсинг. Подставьте свои цифры.
            </p>

            <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <LabeledInputNumber
                label="Численность, чел."
                value={state.emp}
                onChange={(v) => setState({ ...state, emp: v })}
                min={1}
              />
              <LabeledInputNumber
                label="Текущие расходы HR, ₽/мес"
                value={state.hrCost}
                onChange={(v) => setState({ ...state, hrCost: v })}
                min={0}
              />
              <LabeledInputNumber
                label="Сервис, ₽ на 1 чел./мес"
                value={state.ppm}
                onChange={(v) => setState({ ...state, ppm: v })}
                min={0}
              />
              <LabeledInputNumber
                label="Кадровых событий / мес"
                value={state.events}
                onChange={(v) => setState({ ...state, events: v })}
                min={0}
              />
              <LabeledInputNumber
                label="Цена 1 события, ₽"
                value={state.eventFee}
                onChange={(v) => setState({ ...state, eventFee: v })}
                min={0}
              />
              <LabeledInputNumber
                label="ЭДО/1С поддержка, ₽/мес"
                value={state.edoCost}
                onChange={(v) => setState({ ...state, edoCost: v })}
                min={0}
              />
            </div>

            <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <StatCard label="Текущие расходы" value={cf.format(current)} />
              <StatCard label="Сервис INTERIM" value={cf.format(service)} />
              <StatCard label="Экономия, ₽/мес" value={cf.format(Math.max(0, saving))} />
              <StatCard label="Экономия, %" value={`${Math.round(Math.max(0, savingPct) * 100)}%`} />
            </div>
            <p className="mt-2 text-xs text-slate-500">Итоговая смета зависит от процессов, интеграций и SLA.</p>
          </Card>

          <Card>
            <h3 className="text-xl font-extrabold">SLA и отчётность</h3>
            <div className="mt-3 overflow-x-auto">
              <table className="w-full border-collapse text-base">
                <thead>
                  <tr className="text-left text-black">
                    <th className="border-b border-slate-200 px-2 py-2 font-bold">Метрика</th>
                    <th className="border-b border-slate-200 px-2 py-2 font-bold">Цель</th>
                    <th className="border-b border-slate-200 px-2 py-2 font-bold">Комментарий</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border-b border-slate-100 px-2 py-2">Первый ответ</td>
                    <td className="border-b border-slate-100 px-2 py-2">≤ 30 мин</td>
                    <td className="border-b border-slate-100 px-2 py-2">в часы поддержки (9–19, пн–пт)</td>
                  </tr>
                  <tr>
                    <td className="border-b border-slate-100 px-2 py-2">Решение P1/P2/P3</td>
                    <td className="border-b border-slate-100 px-2 py-2">4 ч / 1 р.д. / 3 р.д.</td>
                    <td className="border-b border-slate-100 px-2 py-2">по приоритету заявки</td>
                  </tr>
                  <tr>
                    <td className="border-b border-slate-100 px-2 py-2">Тикеты в срок</td>
                    <td className="border-b border-slate-100 px-2 py-2">≥ 95%</td>
                    <td className="border-b border-slate-100 px-2 py-2">ежемес. отчёт</td>
                  </tr>
                  <tr>
                    <td className="px-2 py-2">ЭДО/приказы</td>
                    <td className="px-2 py-2">ежедневно</td>
                    <td className="px-2 py-2">подпись, архив, поиск</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="mt-2 text-xs text-slate-500">SLA уточняются в договоре и заявке на услуги.</p>
          </Card>
        </div>
      </Section>

      <Section gray>
        <h2 className="mb-4 text-2xl font-black">Пакеты</h2>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          {[
            {
              name: 'Start',
              items: ['КДП базовое (приказы, отпуска, БЛ)', 'Helpdesk HR (e-mail/портал)', 'Еженед. отчёт по заявкам'],
              note: 'Для команд до 100 человек',
            },
            {
              name: 'Growth',
              items: ['Все из Start', 'Табели + обмен с 1С:ЗУП', 'ЭДО (приказы/справки)', 'SLA с приоритетами'],
              note: 'Для 100–500 человек',
            },
            {
              name: 'Enterprise',
              items: ['Все из Growth', 'СКУД интеграция', 'Каталог услуг HR (категории)', 'Расширенная аналитика'],
              note: '500+ и распределённые команды',
            },
          ].map((p) => (
            <Card key={p.name}>
              <h3 className="text-xl font-black">{p.name}</h3>
              <ul className="mt-3 space-y-2 text-slate-700 list-disc pl-5">
                {p.items.map((i) => (
                  <li key={i}>{i}</li>
                ))}
              </ul>
              <p className="mt-3 text-xs text-slate-500">{p.note}</p>
            </Card>
          ))}
        </div>
      </Section>

      <Section>
        <h2 className="mb-4 text-2xl font-black">Процесс запуска</h2>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-5">
          {[
            { n: '1. Диагностика', d: 'Карта процессов, источники данных, роли, риски.' },
            { n: '2. Интеграции', d: '1С:ЗУП, ЭДО, СКУД, HR-портал: настроим обмен.' },
            { n: '3. Регламенты', d: 'SLA, приоритеты P1/P2/P3, шаблоны заявок и писем.' },
            { n: '4. Пилот', d: 'Тест на 1–2 подразделениях, корректировка схем.' },
            { n: '5. Запуск', d: 'Обучение менеджеров, ежемес. отчёты и QBR.' },
          ].map((s) => (
            <Card key={s.n}>
              <h3 className="text-lg font-extrabold">{s.n}</h3>
              <p className="mt-1 text-base text-black">{s.d}</p>
            </Card>
          ))}
        </div>
      </Section>

      <Section gray>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <Card>
            <h3 className="text-xl font-extrabold">Кейс</h3>
            <p className="mt-2 text-base text-black">
              <b>Задача:</b> разгрузить HR-отдел и ускорить ответы сотрудникам.
            </p>
            <p className="mt-1 text-base text-black">
              <b>Решение:</b> Helpdesk HR с SLA, ЭДО приказов, обмен с 1С:ЗУП, каталог услуг.
            </p>
            <p className="mt-1 text-base text-black">
              <b>Результат:</b> первый ответ ≤ 15–30 мин, закрытие 93–97% тикетов в срок; HR сфокусировались на подборе и адаптации.
            </p>
          </Card>
          <Card>
            <h3 className="text-xl font-extrabold">FAQ</h3>
            <details className="group mt-2 border-t border-slate-200 py-3" open>
              <summary className="cursor-pointer text-base font-bold">Нужна ли ваша лицензия/аккредитация?</summary>
              <p className="mt-1 text-sm text-slate-700">
                Для аутсорсинга КДП и helpdesk — нет. Для «предоставления персонала» действуют отдельные требования.
              </p>
            </details>
            <details className="group border-t border-slate-200 py-3">
              <summary className="cursor-pointer text-base font-bold">Работаете с 1С:ЗУП?</summary>
              <p className="mt-1 text-sm text-slate-700">
                Да, настраиваем обмен и сверки. Возможна работа через вашу базу или выделенный контур.
              </p>
            </details>
            <details className="group border-t border-slate-200 py-3">
              <summary className="cursor-pointer text-base font-bold">ЭДО обязателен?</summary>
              <p className="mt-1 text-sm text-slate-700">
                Рекомендуем — ускоряет процессы и снижает риски. Поддерживаем Диадок/СБИС и др.
              </p>
            </details>
          </Card>
        </div>
      </Section>

      <Section id="cta">
        <Card>
          <h3 className="text-xl font-extrabold">Запросить разгрузку HR</h3>
          <form className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <label className="block">
              <span className="mb-1 block text-xs text-slate-500">Имя</span>
              <input
                className="w-full rounded-xl border border-slate-300 px-3 py-2 outline-none focus:border-[#003B9B]"
                placeholder="Как к вам обращаться"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-xs text-slate-500">Телефон</span>
              <input
                className="w-full rounded-xl border border-slate-300 px-3 py-2 outline-none focus:border-[#003B9B]"
                placeholder="+7 () ___--__-__"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              />
            </label>
            <label className="block sm:col-span-2">
              <span className="mb-1 block text-xs text-slate-500">Задача</span>
              <input
                className="w-full rounded-xl border border-slate-300 px-3 py-2 outline-none focus:border-[#003B9B]"
                placeholder="Например: helpdesk HR + ЭДО приказов + табели в 1С:ЗУП"
                value={formData.task}
                onChange={(e) => setFormData({ ...formData, task: e.target.value })}
              />
            </label>
            <label className="block sm:col-span-2">
              <span className="mb-1 block text-xs text-slate-500">Email</span>
              <input
                type="email"
                className="w-full rounded-xl border border-slate-300 px-3 py-2 outline-none focus:border-[#003B9B]"
                placeholder="for@example.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
            </label>

            <label className="flex items-start text-xs sm:text-sm cursor-pointer text-slate-600 sm:col-span-2">
              <input
                type="checkbox"
                checked={formData.agreed}
                onChange={(e) => setFormData({ ...formData, agreed: e.target.checked })}
                className="mt-1 mr-2 h-4 w-4 rounded border-slate-300 text-[#003B9B] focus:ring-[#003B9B]"
              />
              <span>
                Я даю{' '}
                <a href="/privacy" className="text-[#003B9B] underline hover:text-[#FF7C22]">
                  согласие на обработку персональных данных
                </a>{' '}
                и соглашаюсь с{' '}
                <a href="/privacy" className="text-[#003B9B] underline hover:text-[#FF7C22]">
                  Политикой конфиденциальности
                </a>
                .
              </span>
            </label>

            <div className="sm:col-span-2">
              <button
                type="button"
                onClick={handleSubmit}
                className="inline-block rounded-xl bg-[#FF7C22] px-6 py-3 text-sm font-bold text-white shadow hover:brightness-[105%] hover:-translate-y-[1px] transition-all duration-200"
              >
                Отправить
              </button>
              {formStatus && <p className={`mt-2 text-xs ${getStatusColorClass(formStatus)}`}>{formStatus}</p>}
            </div>
          </form>
        </Card>
      </Section>
    </main>
  );
}